import { useCallback, useEffect } from 'react';
import type { SerializeFrom } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import {
    UPDATE_CONVERSATION_API,
    type action,
} from '~/routes/remix-api.chat.update-conversation';
import { useConversationCache } from './use-conversation-cache';

type UpdateConversationResponse = NonNullable<
    SerializeFrom<typeof action>
>['conversation'];

/**
 * Hook to update the settings of a (direct) conversation.
 */
export function useUpdateConversation({
    conversationID,
    onSuccess,
}: {
    conversationID: number;
    onSuccess?: (conversation: UpdateConversationResponse) => void;
}) {
    const { updateCachedConversation } = useConversationCache();
    const fetcher = useFetcher<typeof action>();

    const updateConversation = useCallback(
        ({
            name,
            buildHubID,
        }: {
            name?: string | null;
            buildHubID?: number | null;
        }) => {
            if (conversationID <= 0) return;
            fetcher.submit(
                {
                    conversationID,
                    name: name ?? null,
                    buildHubID: buildHubID ?? null,
                },
                {
                    action: UPDATE_CONVERSATION_API,
                    method: 'POST',
                    encType: 'application/json',
                }
            );
        },
        [fetcher, conversationID]
    );

    // Update the cached conversation if the update conversation mutation succeeds.
    useEffect(() => {
        if (fetcher.data && fetcher.state !== 'submitting') {
            const updatedConversation = fetcher.data.conversation;
            const systemMessages = fetcher.data.systemMessages;
            updateCachedConversation(Number(updatedConversation.ID), {
                Name: updatedConversation.Name ?? null,
                BuildHub: updatedConversation.BuildHub ?? null,
                Messages: systemMessages,
            });
            onSuccess?.(updatedConversation);
        }
        // Intentially do not include `onSuccess` to avoid unnecessary re-renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher, updateCachedConversation]);

    return {
        /**
         * The update conversation fetcher is submitting.
         */
        isSubmitting: fetcher.state === 'submitting',
        /**
         * Update the conversation settings. Can update the following fields:
         * - Name (title).
         * - BuildHubID. Set to `0` to unlink the project.
         */
        updateConversation,
    };
}
